import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { t } from "../../locales";
import { PUT } from "../../services/axios.service";
import { useToastHandler } from "../../hooks/useToastHandler";
import { useAppDispatch } from "../../app/hooks";
import { fetchLoggingUserData } from "../../features/loggingUser/loggingUserSlice";
import { LoggingUserProps } from "../../types/user";

type Props = {
  loggingUser: LoggingUserProps;
};

type FormData = {
  name: string;
  email: string;
};

const UserProfileForm = ({ loggingUser }: Props) => {
  const dispatch = useAppDispatch();
  const { handleSuccess, handleError } = useToastHandler();
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  useEffect(() => {
    if (loggingUser?.name || loggingUser?.email) {
      reset({
        name: loggingUser.name || "",
        email: loggingUser.email || "",
      });
    }
  }, [loggingUser, reset]);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const response: any = await PUT("/v1/update-profile", {
        name: data.name,
        email: data.email,
      });
      if (response.data.status === "success") {
        handleSuccess(response);
        dispatch(fetchLoggingUserData());
      } else {
        handleError(response);
      }
    } catch (error: any) {
      handleError(error);
    }
    setIsLoading(false);
  };

  const initials = loggingUser?.name
    ? loggingUser.name
        .split(" ")
        .map((w: string) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "U";

  const inputBase =
    "w-full h-12 pl-12 pr-4 text-sm text-brand-dark bg-white border-2 rounded-xl outline-none box-border transition-[border-color,box-shadow] duration-200 placeholder:text-gray-400 focus:border-brand-dark focus:shadow-focus-brand read-only:bg-gray-100 read-only:text-gray-500 read-only:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed";

  return (
    <div className="bg-white rounded-2xl shadow-card p-8">
      {/* Avatar */}
      <div className="w-20 h-20 rounded-full bg-[#113F67] flex items-center justify-center mb-8">
        <span className="text-white text-2xl font-bold">{initials}</span>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* 3-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
          {/* Username */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-brand-dark mb-[7px]">
              {t.profile.nameLabel}<span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-[14px] top-1/2 -translate-y-1/2 text-brand-light pointer-events-none">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </span>
              <input
                className={`${inputBase} ${errors.name ? "border-red-400 shadow-focus-error" : "border-gray-200"}`}
                type="text"
                placeholder={t.profile.namePlaceholder}
                {...register("name", { required: true })}
              />
            </div>
            {errors.name && errors.name.type === "required" && (
              <span className="text-sm text-red-500 mt-1 flex items-center gap-1">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {t.profile.nameRequired}
              </span>
            )}
          </div>

          {/* Email */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-brand-dark mb-[7px]">
              {t.profile.emailLabel}<span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-[14px] top-1/2 -translate-y-1/2 text-brand-light pointer-events-none">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </span>
              <input
                className={`${inputBase} ${errors.email ? "border-red-400 shadow-focus-error" : "border-gray-200"}`}
                type="email"
                placeholder={t.profile.emailPlaceholder}
                {...register("email", { required: true })}
              />
            </div>
            {errors.email && errors.email.type === "required" && (
              <span className="text-sm text-red-500 mt-1 flex items-center gap-1">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {t.profile.emailRequired}
              </span>
            )}
          </div>

          {/* Phone (read-only) */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-brand-dark mb-[7px]">
              {t.profile.phoneLabel}
            </label>
            <div className="relative">
              <span className="absolute left-[14px] top-1/2 -translate-y-1/2 text-brand-light pointer-events-none">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.72-.87a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </span>
              <input
                className={`${inputBase} border-gray-200`}
                type="text"
                value={loggingUser?.phone || ""}
                readOnly
              />
            </div>
          </div>
        </div>

        {/* Right-aligned submit */}
        <div className="flex justify-end">
          <button
            className="px-8 py-3 bg-[#113F67] text-white font-semibold rounded-lg transition-colors duration-200 hover:bg-[#0d2f4d] disabled:opacity-60 disabled:cursor-not-allowed"
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin shrink-0"></span>
                {t.profile.updatingProfile}
              </div>
            ) : (
              t.profile.updateProfileButton
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserProfileForm;
